import {FC, lazy, memo, useEffect, useMemo, useState} from 'react';
import { Colors, FlexBox, useBreakpoint, useIsMobile, useScale } from '@brg/ui';
import { Project } from '@brg/api';
import { useTheme } from 'styled-components';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {getLongreadId, getLongreadSign, getTeamLgId} from "../project/sections/ps-longread";
import {ApplicationShared, LayoutConst, useModal} from "../../../core";
import { ICoverContent } from '../exploreHseContextsNew/const/coverContent';
import { IVotesContent } from '../exploreHseContextsNew/const/votesContent';
import { AppTester } from 'supporting';
import {ITeamFilterModel} from "../teams/components/TeamFilterEditView";
import {ModerationType} from "../teams/const/ModerationTypes";
import { LongreadWrapper } from './styles';
import {useParams, useRouteMatch} from "react-router-dom";
import {BookDelegate} from "../../../_deziiign/books/core/book-delegate";
import { IAddProjectCoverContent } from './components/PresentatitonBlock/types/AdditionalContentType';
import { getProjectHrefPrefix } from './support/getProjectHrefPrefix';
import { navigationHandler } from './support/navigationHandler';
import { useGetFriendlyUrlParam } from 'shared/hooks/useGetFriendlyUrlParam';
import { isServer } from 'shared/constants/isServer';
// Lazy-load values from 'brg-longread' instead of importing statically

const LongRead = isServer ? FlexBox : require('brg-longread').LongRead;
const LongreadAllowedActions = isServer ? {} : require('brg-longread').LongreadAllowedActions;

type PropsT = {
  raw: Project<'Global'> & any;
  longReadLength: number;
  setLongReadLength: (length: number) => void;
  toggleMobileEditor?: (val: boolean) => void;
  onSetMobTextLength?: (val: number) => void;
  hasSpecialSourcesBlocks?: boolean;
  isEditable?: boolean;
  disableVoting?: boolean;
  mobileEditorAction?: string;
  lgid?: string;
  isTeamLg?: boolean;
  isThemeEditor?: boolean;
  isLMSMain?: boolean;
  coverContent?: ICoverContent;
  votesContent?: IVotesContent;
  additionalContent?: IAddProjectCoverContent;
  titleNewPosition?: string;
  teamFilter?: ITeamFilterModel;
  setResetLdIds?: (val: string[]) => void;
  resetLdIds?: string[];
  onSetThemeAnchors?: (val: any) => void;
  onSetAnchors?: (val: any) => void;
  setLongreadStyle?: (val: any) => void;
  lmsModuleInfo?: any;
  isLMSExamEdit?: boolean;
  authorId?: string;
  themeIndex?: number;
};

const LongReadBlock: FC<PropsT> = (props) => {

  const {
    raw,
    teamFilter,
    isEditable = true,
    lgid,
    disableVoting,
    isTeamLg,
    longReadLength,
    setLongReadLength,
    coverContent,
    votesContent,
    hasSpecialSourcesBlocks = false,
    additionalContent,
    titleNewPosition,
    mobileEditorAction = '',
    toggleMobileEditor,
    onSetMobTextLength,
    resetLdIds,
    isThemeEditor,
    setResetLdIds,
    onSetThemeAnchors,
    isLMSMain,
    onSetAnchors,
    lmsModuleInfo,
    isLMSExamEdit,
    authorId,
    themeIndex,
    setLongreadStyle
  } = props

  const s = useScale();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { i18n: { language } } = useTranslation();
  const is940px = useBreakpoint(LayoutConst.screen.md);
  const is744px = useBreakpoint(LayoutConst.screen.s);
  const isProHSE = AppTester.isProHSE();
  const isPro = AppTester.isProDeziiign();
  const modal = useModal();
  const id = useGetFriendlyUrlParam("id");
  const isTeamEdit = useRouteMatch('/team-edit');
  
  const isLMSProject = useMemo(() => {
    return raw?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item))
  }, [raw])

  const teamModerationType = useMemo(() => {
    return raw?.moderationType || null
  }, [raw])

  const canAddAnchors = useMemo(() => {
    return raw?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item))
  }, [raw])

  const vtype = useSelector((state: any) => state.vt.vtype);

  const longreadId = lgid ? lgid : isTeamLg ? getTeamLgId(raw, language) : getLongreadId(raw, language);
  const longreadSign = getLongreadSign(raw, language);

  const isLightTheme = theme.projectView.titleColor === Colors.galBlack100;
  const isDarkTheme = AppTester.isSuperPuper()

  const colorStyle = isDarkTheme ? "dark" : isLightTheme ? 'light' : 'grey';

  const styles = useMemo(() => ({
    maxWidth: isMobile || ['fluid', 't-shape', 'fixed-ratio-large', 'free-type'].includes(String(vtype)) ? '100%' : s(1152) + 'px',
    width: '100%',
    padding: (!is744px && is940px) ? "0 36px" : 
    // ((isPro || isProHSE )? 
    '0 10px' 
    // : '0')
    ,
    margin: '0 auto'
  }), [isMobile, s, vtype, isProHSE, isPro]);

  const currentTeamId = useMemo(() => {
    if (!raw?.teams || raw?.teams?.length === 0) return '';

    return raw?.teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || ''
  }, [raw?.teams])

  const allowedActions = useMemo(() => {
    if (AppTester.isStartHSE() && LongreadAllowedActions) {
      return [
        LongreadAllowedActions.image,
        LongreadAllowedActions.colored,
        LongreadAllowedActions.buttonLink
      ]
    }
    return []
  } , [AppTester, isLMSProject, LongreadAllowedActions])
  
  return (
    <LongreadWrapper
      width="100%"
      offsetBottom={80}
      style={styles}
    >
      {raw && (
        <LongRead
          bookId={(BookDelegate.isPaid(raw) && raw?.book?.bookId) ? raw?.book?.bookId : undefined}
          includedCards={raw?.includedCards || []}
          hasSpecialSourcesBlocks={hasSpecialSourcesBlocks}
          colorStyle={colorStyle}
          onChangeBlocksLength={setLongReadLength}
          projectId={raw?.projectId}
          authorId={authorId}
          allowedActions={allowedActions}
          areaId=""
          isEditable={isEditable}
          countryId=""
          type=""
          isLMS={isLMSProject}
          teamModerationType={teamModerationType}
          longreadId={longreadId}
          linkTo="geography"
          shouldDisplayError={longReadLength === 0}
          theme={theme}
          viewType={vtype}
          appName={ApplicationShared.application}
          lang={language}
          mobileEditorAction={mobileEditorAction}
          toggleMobileEditor={toggleMobileEditor}
          onSetMobTextLength={onSetMobTextLength} 
          disableVoting={disableVoting}
          coverContent={coverContent}
          votesContent={votesContent}
          additionalContent={additionalContent}
          titleNewPosition={titleNewPosition}
            //временный костыль для теста
          typeConnection={(process.env.REACT_APP_API_CONNECTION_MODE === 'develop' || process.env.REACT_APP_API_CONNECTION_MODE === 'staging') ? 'develop' : process.env.REACT_APP_API_CONNECTION_MODE}
          teamContexts={(isTeamLg || isThemeEditor) ? undefined : raw?.teams?.map((team: any) => team?.contextName) || []}
          teamId={(id && isTeamEdit) ? id : isThemeEditor ? currentTeamId : undefined}
          teamExtraFilter={isLMSProject ? true  : isTeamLg ? teamFilter : (isPro || isProHSE) ? true : undefined}
          onOpenSetProjectsModal={(props: any) => modal.show('send-project-longread', {
            ...props, 
            project: raw,
            hideBookmark: true,
            ...(isTeamLg ? { externalParams: {
              authorOrCoAuthorIds: []
            }} : {})
            // onlyAuthorProjects: true 
          })}
          canAddAnchors={canAddAnchors}
          needToResetIds={resetLdIds}
          setNeedToResetIds={setResetLdIds}
          isThemeEditor={isThemeEditor}
          onSetThemeAnchors={onSetThemeAnchors}
          isLMSMain={isLMSMain}
          onSetAnchors={onSetAnchors}
          sign={longreadSign}
          lmsModuleInfo={lmsModuleInfo}
          specBlockProps={{ getProjectHrefPrefix, navigationHandler }}
          isLMSExamEdit={isLMSExamEdit}
          themeIndex={themeIndex}
          setLongreadStyle={setLongreadStyle}
        />
      )}
    </LongreadWrapper>
  );
};

export default memo(LongReadBlock);
import {FC, lazy, memo, useEffect, useMemo, useState} from 'react';
import { Colors, FlexBox, useBreakpoint, useIsMobile, useScale } from '@brg/ui';
import { Project } from '@brg/api';
import { useTheme } from 'styled-components';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {getLongreadId, getLongreadSign, getTeamLgId} from "../project/sections/ps-longread";
import {ApplicationShared, LayoutConst, useModal} from "../../../core";
import { ICoverContent } from '../exploreHseContextsNew/const/coverContent';
import { IVotesContent } from '../exploreHseContextsNew/const/votesContent';
import { AppTester } from 'supporting';
import {ITeamFilterModel} from "../teams/components/TeamFilterEditView";
import {ModerationType} from "../teams/const/ModerationTypes";
import { LongreadWrapper } from './styles';
import {useParams, useRouteMatch} from "react-router-dom";
import {BookDelegate} from "../../../_deziiign/books/core/book-delegate";
import { IAddProjectCoverContent } from './components/PresentatitonBlock/types/AdditionalContentType';
import { getProjectHrefPrefix } from './support/getProjectHrefPrefix';
import { navigationHandler } from './support/navigationHandler';
import { useGetFriendlyUrlParam } from 'shared/hooks/useGetFriendlyUrlParam';
import { isServer } from 'shared/constants/isServer';
// Lazy-load values from 'brg-longread' instead of importing statically

const LongRead = isServer ? FlexBox : require('brg-longread').LongRead;
const LongreadAllowedActions = isServer ? {} : require('brg-longread').LongreadAllowedActions;

type PropsT = {
  raw: Project<'Global'> & any;
  longReadLength: number;
  setLongReadLength: (length: number) => void;
  toggleMobileEditor?: (val: boolean) => void;
  onSetMobTextLength?: (val: number) => void;
  hasSpecialSourcesBlocks?: boolean;
  isEditable?: boolean;
  disableVoting?: boolean;
  mobileEditorAction?: string;
  lgid?: string;
  isTeamLg?: boolean;
  isThemeEditor?: boolean;
  isLMSMain?: boolean;
  coverContent?: ICoverContent;
  votesContent?: IVotesContent;
  additionalContent?: IAddProjectCoverContent;
  titleNewPosition?: string;
  teamFilter?: ITeamFilterModel;
  setResetLdIds?: (val: string[]) => void;
  resetLdIds?: string[];
  onSetThemeAnchors?: (val: any) => void;
  onSetAnchors?: (val: any) => void;
  setLongreadStyle?: (val: any) => void;
  lmsModuleInfo?: any;
  isLMSExamEdit?: boolean;
  authorId?: string;
  themeIndex?: number;
};

const LongReadBlock: FC<PropsT> = (props) => {

  const {
    raw,
    teamFilter,
    isEditable = true,
    lgid,
    disableVoting,
    isTeamLg,
    longReadLength,
    setLongReadLength,
    coverContent,
    votesContent,
    hasSpecialSourcesBlocks = false,
    additionalContent,
    titleNewPosition,
    mobileEditorAction = '',
    toggleMobileEditor,
    onSetMobTextLength,
    resetLdIds,
    isThemeEditor,
    setResetLdIds,
    onSetThemeAnchors,
    isLMSMain,
    onSetAnchors,
    lmsModuleInfo,
    isLMSExamEdit,
    authorId,
    themeIndex,
    setLongreadStyle
  } = props

  const s = useScale();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { i18n: { language } } = useTranslation();
  const is940px = useBreakpoint(LayoutConst.screen.md);
  const is744px = useBreakpoint(LayoutConst.screen.s);
  const isProHSE = AppTester.isProHSE();
  const isPro = AppTester.isProDeziiign();
  const modal = useModal();
  const id = useGetFriendlyUrlParam("id");
  const isTeamEdit = useRouteMatch('/team-edit');
  
  const isLMSProject = useMemo(() => {
    return raw?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item))
  }, [raw])

  const teamModerationType = useMemo(() => {
    return raw?.moderationType || null
  }, [raw])

  const canAddAnchors = useMemo(() => {
    return raw?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item))
  }, [raw])

  const vtype = useSelector((state: any) => state.vt.vtype);

  const longreadId = lgid ? lgid : isTeamLg ? getTeamLgId(raw, language) : getLongreadId(raw, language);
  const longreadSign = getLongreadSign(raw, language);

  const isLightTheme = theme.projectView.titleColor === Colors.galBlack100;
  const isDarkTheme = AppTester.isSuperPuper()

  const colorStyle = isDarkTheme ? "dark" : isLightTheme ? 'light' : 'grey';

  const styles = useMemo(() => ({
    maxWidth: isMobile || ['fluid', 't-shape', 'fixed-ratio-large', 'free-type'].includes(String(vtype)) ? '100%' : s(1152) + 'px',
    width: '100%',
    padding: (!is744px && is940px) ? "0 36px" : 
    // ((isPro || isProHSE )? 
    '0 10px' 
    // : '0')
    ,
    margin: '0 auto'
  }), [isMobile, s, vtype, isProHSE, isPro]);

  const currentTeamId = useMemo(() => {
    if (!raw?.teams || raw?.teams?.length === 0) return '';

    return raw?.teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || ''
  }, [raw?.teams])

  const allowedActions = useMemo(() => {
    if (AppTester.isStartHSE() && LongreadAllowedActions) {
      return [
        LongreadAllowedActions.image,
        LongreadAllowedActions.colored,
        LongreadAllowedActions.buttonLink
      ]
    }
    return []
  } , [AppTester, isLMSProject, LongreadAllowedActions])
  
  return (
    <LongreadWrapper
      width="100%"
      offsetBottom={80}
      style={styles}
    >
      {raw && (
        <LongRead
          bookId={(BookDelegate.isPaid(raw) && raw?.book?.bookId) ? raw?.book?.bookId : undefined}
          includedCards={raw?.includedCards || []}
          hasSpecialSourcesBlocks={hasSpecialSourcesBlocks}
          colorStyle={colorStyle}
          onChangeBlocksLength={setLongReadLength}
          projectId={raw?.projectId}
          authorId={authorId}
          allowedActions={allowedActions}
          areaId=""
          isEditable={isEditable}
          countryId=""
          type=""
          isLMS={isLMSProject}
          teamModerationType={teamModerationType}
          longreadId={longreadId}
          linkTo="geography"
          shouldDisplayError={longReadLength === 0}
          theme={theme}
          viewType={vtype}
          appName={ApplicationShared.application}
          lang={language}
          mobileEditorAction={mobileEditorAction}
          toggleMobileEditor={toggleMobileEditor}
          onSetMobTextLength={onSetMobTextLength} 
          disableVoting={disableVoting}
          coverContent={coverContent}
          votesContent={votesContent}
          additionalContent={additionalContent}
          titleNewPosition={titleNewPosition}
            //временный костыль для теста
          typeConnection={(process.env.REACT_APP_API_CONNECTION_MODE === 'develop' || process.env.REACT_APP_API_CONNECTION_MODE === 'staging') ? 'develop' : process.env.REACT_APP_API_CONNECTION_MODE}
          teamContexts={(isTeamLg || isThemeEditor) ? undefined : raw?.teams?.map((team: any) => team?.contextName) || []}
          teamId={(id && isTeamEdit) ? id : isThemeEditor ? currentTeamId : undefined}
          teamExtraFilter={isLMSProject ? true  : isTeamLg ? teamFilter : (isPro || isProHSE) ? true : undefined}
          onOpenSetProjectsModal={(props: any) => modal.show('send-project-longread', {
            ...props, 
            project: raw,
            hideBookmark: true,
            ...(isTeamLg ? { externalParams: {
              authorOrCoAuthorIds: []
            }} : {})
            // onlyAuthorProjects: true 
          })}
          canAddAnchors={canAddAnchors}
          needToResetIds={resetLdIds}
          setNeedToResetIds={setResetLdIds}
          isThemeEditor={isThemeEditor}
          onSetThemeAnchors={onSetThemeAnchors}
          isLMSMain={isLMSMain}
          onSetAnchors={onSetAnchors}
          sign={longreadSign}
          lmsModuleInfo={lmsModuleInfo}
          specBlockProps={{ getProjectHrefPrefix, navigationHandler }}
          isLMSExamEdit={isLMSExamEdit}
          themeIndex={themeIndex}
          setLongreadStyle={setLongreadStyle}
        />
      )}
    </LongreadWrapper>
  );
};

export default memo(LongReadBlock);
